/**
 * Number formats the Excel export gives its value cells (M4A2A-570), and the one reader that turns
 * such a cell back into the text the file used to hold.
 *
 * The export used to write every percentage, mean, median and NPS score as text ("45% AB"), so
 * Excel flagged each one "Number stored as text". They are numbers now, with a number format that
 * shows the same text: a percentage is a fraction under `0%` / `0.0%`, significance letters ride in
 * the format itself (`0%" AB"`), an NPS score is `+0;-0;0`, a mean `0.00`, a median `0`.
 *
 * `displayOfNumber` is the inverse, for readers that work on text (the iOOH report builder reads a
 * cell as "72% Da" to find its letters). Pure: no exceljs, no app types.
 */

export const NPS_NUM_FMT = '+0;-0;0';
export const MEAN_NUM_FMT = '0.00';
export const MEDIAN_NUM_FMT = '0';

/** A format that shows `marks` (significance letters or arrows) after the number, in every section. */
export function withMarks(numFmt: string, marks = ''): string {
    if (!marks) return numFmt;
    const quoted = `" ${marks.replace(/"/g, '""')}"`;
    return numFmt
        .split(';')
        .map((section) => section + quoted)
        .join(';');
}

/** `0%`, `0.0%` or `0.00%`, with the cell's significance letters shown after the number. */
export function percentNumFmt(decimalPlaces: number, letters = ''): string {
    return withMarks(decimalPlaces > 0 ? `0.${'0'.repeat(decimalPlaces)}%` : '0%', letters);
}

/** A percentage on the 0-100 scale as the fraction Excel stores, rounded to what the format shows. */
export function pctToFraction(pct: number, decimalPlaces: number): number {
    const shown = Number(pct.toFixed(decimalPlaces));
    return Number((shown / 100).toFixed(decimalPlaces + 2));
}

const PERCENT_FMT = /^0(?:\.(0+))?%$/;
const MARKS_SUFFIX = /" ((?:[^"]|"")*)"$/;

/** A format split into its number part and the marks it shows after the number. */
function splitMarks(numFmt: string): { base: string; marks: string } {
    const sections = numFmt.split(';');
    const m = MARKS_SUFFIX.exec(sections[0]);
    if (!m) return { base: numFmt, marks: '' };
    const strip = (section: string) => section.replace(MARKS_SUFFIX, '');
    return { base: sections.map(strip).join(';'), marks: m[1].replace(/""/g, '"') };
}

/**
 * The text a number shows under one of the formats above, or `String(value)` for any other format.
 * "72% Da" for 0.72 under `0%" Da"`, "+12" for 12 under `+0;-0;0`, "3.46" under `0.00`.
 */
export function displayOfNumber(value: number, numFmt: string | undefined): string {
    if (!numFmt || numFmt === 'General') return String(value);
    const { base, marks } = splitMarks(numFmt);
    const text = numberText(value, base);
    return marks ? `${text} ${marks}` : text;
}

function numberText(value: number, numFmt: string): string {
    const pct = PERCENT_FMT.exec(numFmt);
    if (pct) return `${(value * 100).toFixed(pct[1]?.length ?? 0)}%`;
    if (numFmt === NPS_NUM_FMT)
        return value > 0 ? `+${Math.round(value)}` : String(Math.round(value));
    const fixed = /^0\.(0+)$/.exec(numFmt);
    if (fixed) return value.toFixed(fixed[1].length);
    if (numFmt === '0') return value.toFixed(0);
    return String(value);
}
